import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

const verticalReels = [
  {
    src: "https://customer-assets.emergentagent.com/job_disruptiva-redesign/artifacts/1e9nfqqs_ae550a591150487e89823e05614b9cbf.MOV",
    label: "Behind the Scenes",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_disruptiva-redesign/artifacts/lwwcw5lk_b88da06c1a41ae46ee68cbfeae4ef839ec90d2646c1fa409d5d6c002a2d71075.MP4",
    label: "Producción en Set",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_disruptiva-redesign/artifacts/bh121sau_copy_A9FF8CBE-F4A5-490F-BA61-4130F2BF050D.MOV",
    label: "Cobertura de Evento",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_disruptiva-redesign/artifacts/fkrk1xii_Reel%203.MOV",
    label: "Reel Creativo",
  },
];

const ReelCard = ({ reel, index }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const handleMouseEnter = () => {
    const v = videoRef.current;
    if (v) {
      v.play().catch(() => {});
      setPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    const v = videoRef.current;
    if (v) {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="reel-card group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`reel-card-${index}`}
    >
      <div className="reel-card-inner">
      <video
        ref={videoRef}
        src={reel.src}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        className="reel-video"
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 pointer-events-none z-10 rounded-[15px]" />

      {/* Play/Pause center button */}
      <button
        onClick={togglePlay}
        className="absolute inset-0 z-20 flex items-center justify-center"
        data-testid={`reel-play-toggle-${index}`}
      >
        <div className={`w-12 h-12 rounded-full bg-white/[0.08] border border-white/[0.12] backdrop-blur-md flex items-center justify-center transition-all duration-300 ${playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}>
          {playing ? (
            <Pause className="w-5 h-5 text-white" />
          ) : (
            <Play className="w-5 h-5 text-white ml-0.5" />
          )}
        </div>
      </button>

      {/* Mute toggle */}
      <button
        onClick={(e) => { e.stopPropagation(); setMuted(!muted); }}
        className="absolute bottom-14 right-3 z-30 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/50 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
        data-testid={`reel-mute-${index}`}
      >
        {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
      </button>

      {/* Label */}
      <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
        <span className="text-[11px] text-purple-400/50 font-space tracking-widest uppercase">Reel</span>
        <p className="text-sm text-white/80 font-space font-medium mt-0.5">{reel.label}</p>
      </div>

      {/* Progress line */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-purple-500/20 z-30 rounded-b-[15px] overflow-hidden">
        <div className={`h-full bg-purple-500/50 transition-all ${playing ? "animate-pulse" : ""}`} style={{ width: playing ? "100%" : "0%" }} />
      </div>
      </div>
    </motion.div>
  );
};

export const VideoShowcase = () => {
  const scrollRef = useRef(null);

  return (
    <section className="relative py-24 overflow-hidden" data-testid="video-showcase">
      <div className="morph-bg w-[400px] h-[400px] bg-purple-700/6 bottom-[10%] left-[-5%]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="pulse-dot" />
            <span className="text-[13px] text-white/30 font-space tracking-widest uppercase">
              Reels & Motion
            </span>
          </div>
          <h2 className="font-space font-bold text-3xl md:text-5xl text-white leading-tight">
            En movimiento
          </h2>
        </motion.div>

        {/* Horizontal scroll of vertical reels */}
        <div
          ref={scrollRef}
          className="reels-scroll-container"
          data-testid="reels-scroll"
        >
          {verticalReels.map((reel, i) => (
            <ReelCard key={i} reel={reel} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
